import { AuthController } from '@ayocore/platform/identity/client/AuthController';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';
import { IDENTITY_ROLES } from '@ayocore/platform/identity/common/roles';
import type { AcademyRole } from '../common/roles';

/** Есть ли у списка ролей хотя бы одна из разрешённых (ADMIN разрешён всегда). */
function matches(roles: readonly string[], allowed: AcademyRole[]): boolean {
  return roles.includes(IDENTITY_ROLES.ADMIN) || allowed.some(role => roles.includes(role));
}

/**
 * Реактивная проверка ролей академии для компонентов — тот же критерий, что и в
 * guard'ах меню, но с подпиской на смену пользователя.
 *
 * ⚠️ Скрывает управление в интерфейсе, но не защищает операцию. Реальную
 * проверку делает сервер: репозиторные методы — через RBAC, RPC-функции —
 * вручную в теле функции.
 *
 * @param allowed Роли академии, которым действие доступно
 */
export function useHasAnyAcademyRole(...allowed: AcademyRole[]): boolean {
  const roles = useAuthController(state => state.user?.roles ?? []);
  return matches(roles, allowed);
}

/**
 * Guard для пунктов меню академии: пункт виден, если у текущего пользователя
 * есть хотя бы одна из перечисленных ролей.
 *
 * Платформенная роль ADMIN учитывается всегда — сервер для неё пропускает все
 * RBAC-проверки (см. PermissionService), поэтому прятать от админа пункты
 * академии было бы неверно.
 *
 * ⚠️ Guard управляет только видимостью в меню. Реальный доступ проверяет
 * сервер при каждом RPC-вызове.
 *
 * @param allowed Роли академии, которым пункт доступен
 * @returns Предикат для поля `guard` в NavigationItem
 */
export function hasAnyAcademyRole(...allowed: AcademyRole[]): () => boolean {
  return () => matches(AuthController.getInstance().getSnapshot().user?.roles ?? [], allowed);
}
